import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import api from '../services/api';

const PERSIAN_DAYS = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه'];
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function toMinutes(time) {
  const [h, m] = (time || '00:00').split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

function ConsultantWeeklySchedule({ consultant }) {
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [workingHours, setWorkingHours] = useState([]);
  const [unavailableSlots, setUnavailableSlots] = useState([]);
  const [bookedByDate, setBookedByDate] = useState({});
  const [appointmentsByDate, setAppointmentsByDate] = useState({});
  const [sessionDuration, setSessionDuration] = useState(60);

  const consultantId = consultant?._id || consultant?.id;

  // شروع هفته (شنبه تا جمعه)
  const weekDays = useMemo(() => {
    const today = new Date();
    const dow = today.getDay(); // 0=Sunday ... 6=Saturday
    const daysFromSaturday = (dow + 1) % 7; // Saturday index 0
    const saturday = new Date(today);
    saturday.setDate(today.getDate() - daysFromSaturday);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(saturday);
      d.setDate(saturday.getDate() + i);
      return d;
    });
  }, []);

  // محاسبه پویای ساعات کاری بر اساس داده‌های مشاور
  const dynamicHours = useMemo(() => {
    if (!workingHours || workingHours.length === 0) {
      return []; // اگر داده‌ای وجود ندارد، آرایه خالی برگردان
    }

    // پیدا کردن حداقل startTime و حداکثر endTime از روزهای کاری
    let minStart = Infinity;
    let maxEnd = 0;

    workingHours.forEach((wh) => {
      if (wh.isWorking && wh.startTime && wh.endTime) {
        const startMin = toMinutes(wh.startTime);
        const endMin = toMinutes(wh.endTime);
        
        if (startMin < minStart) minStart = startMin;
        if (endMin > maxEnd) maxEnd = endMin;
      }
    });

    if (minStart === Infinity || maxEnd === 0) {
      return [];
    }

    // تولید اسلات‌های زمانی بر اساس sessionDuration
    const slots = [];
    let current = minStart;
    
    while (current + sessionDuration <= maxEnd) {
      slots.push({
        start: current,
        end: current + sessionDuration,
        timeStr: minutesToTime(current),
      });
      current += sessionDuration;
    }

    return slots;
  }, [workingHours, sessionDuration]);

  // فیلتر کردن روزهای کاری برای نمایش در هدر جدول
  const workingDays = useMemo(() => {
    return weekDays.map((d, idx) => {
      const dayName = DAY_NAMES[d.getDay()];
      const schedule = workingHours.find((wh) => wh.day === dayName);
      return {
        date: d,
        dayName,
        isWorking: schedule?.isWorking || false,
        schedule,
      };
    });
  }, [weekDays, workingHours]);

  useEffect(() => {
    if (!consultantId) return;

    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        // ساعات کاری و نام مشاور
        const whRes = await api.get(`/users/${consultantId}/working-hours`);
        const whData = whRes.data?.data || {};
        setWorkingHours(whData.workingHours || []);
        setSessionDuration(whData.sessionDuration || 60);

        // ساعات غیرفعال (تاریخی)
        const unavRes = await api.get(`/users/${consultantId}/unavailable-slots`);
        const unavData = unavRes.data?.data || {};
        setUnavailableSlots(unavData.unavailableSlots || []);

        // اسلات‌های رزرو شده و جزئیات نوبت‌ها برای هر روز هفته
        const bookedMap = {};
        const appointmentsMap = {};

        for (const d of weekDays) {
          const dateStr = d.toISOString().split('T')[0];
          try {
            const res = await api.post('/appointments/booked-slots', {
              consultantId,
              date: dateStr,
            });
            const data = res.data?.data || {};
            const slots = data.bookedSlots || data.slots || [];
            bookedMap[dateStr] = slots.map((t) => (t && t.length === 4 ? '0' + t : t));
            appointmentsMap[dateStr] = data.appointments || [];
          } catch (e) {
            bookedMap[dateStr] = [];
            appointmentsMap[dateStr] = [];
          }
        }

        setBookedByDate(bookedMap);
        setAppointmentsByDate(appointmentsMap);
      } catch (e) {
        console.error('Error loading weekly schedule:', e);
        setError(e?.message || 'خطا در دریافت جدول هفتگی');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [consultantId, weekDays]);

  const getStatusForSlot = (date, slot) => {
    const dateStr = date.toISOString().split('T')[0];
    const jsDow = date.getDay(); // 0=Sunday ...
    const dayName = DAY_NAMES[jsDow];
    const schedule = workingHours.find((wh) => wh.day === dayName);

    const slotStart = slot.start;
    const slotEnd = slot.end;

    // اگر روز کاری نیست
    if (!schedule || !schedule.isWorking) {
      return { label: 'غیرفعال', color: 'gray' };
    }

    const startMinutes = toMinutes(schedule.startTime);
    const endMinutes = toMinutes(schedule.endTime);

    // اگر خارج از ساعت کاری است
    if (slotStart < startMinutes || slotEnd > endMinutes) {
      return { label: 'غیرفعال', color: 'gray' };
    }

    // استراحت قدیمی
    const legacyBreaks = [];
    if (schedule.breakStart && schedule.breakEnd) {
      legacyBreaks.push({
        start: toMinutes(schedule.breakStart),
        end: toMinutes(schedule.breakEnd),
      });
    }

    // استراحت‌های جدید
    const newBreaks = Array.isArray(schedule.breaks)
      ? schedule.breaks
          .filter((b) => b && b.startTime && b.endTime)
          .map((b) => ({ start: toMinutes(b.startTime), end: toMinutes(b.endTime) }))
      : [];

    const allBreaks = [...legacyBreaks, ...newBreaks];

    const inBreak = allBreaks.some((b) => slotStart < b.end && slotEnd > b.start);
    if (inBreak) {
      return { label: 'استراحت', color: 'yellow' };
    }

    // غیرفعال تاریخی
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    const unavForDay = (unavailableSlots || []).filter((s) => {
      const d = new Date(s.date);
      return d >= dayStart && d <= dayEnd;
    });

    const inUnavailable = unavForDay.some((s) => {
      const uStart = toMinutes(s.startTime);
      const uEnd = toMinutes(s.endTime);
      return slotStart < uEnd && slotEnd > uStart;
    });

    if (inUnavailable) {
      return { label: 'غیرفعال', color: 'orange' };
    }

    // رزرو شده
    const bookedSlots = bookedByDate[dateStr] || [];
    const normalizedSlot = slot.timeStr.length === 4 ? '0' + slot.timeStr : slot.timeStr;

    const isBooked = bookedSlots.some(
      (t) => (t && t.length === 4 ? '0' + t : t) === normalizedSlot || t === slot.timeStr
    );

    if (isBooked) {
      const dayAppointments = appointmentsByDate[dateStr] || [];
      const appointment = dayAppointments.find(
        (a) => (a.time && a.time.length === 4 ? '0' + a.time : a.time) === normalizedSlot || a.time === slot.timeStr
      );

      return {
        label: 'رزرو شده',
        color: 'gray',
        appointment: appointment ? { clientName: appointment.clientName } : null,
      };
    }

    return { label: 'آزاد', color: 'green' };
  };

  if (!consultantId) {
    return <Text fontSize="sm">مشاور انتخاب نشده است.</Text>;
  }

  if (loading) {
    return (
      <Flex align="center" justify="center" minH="200px">
        <Spinner mr={3} />
        <Text>در حال بارگذاری جدول هفتگی...</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Box>
        <Text color="red.500" fontSize="sm">
          {error}
        </Text>
      </Box>
    );
  }

  // اگر هیچ ساعت کاری وجود ندارد
  if (dynamicHours.length === 0) {
    return (
      <Box>
        <Text fontWeight="bold" mb={3} color={textColor}>
          جدول هفتگی (هفته جاری)
        </Text>
        <Text color="gray.500">هیچ ساعت کاری تعریف نشده است.</Text>
      </Box>
    );
  }

  return (
    <Box overflowX="auto">
      <Text fontWeight="bold" mb={3} color={textColor}>
        جدول هفتگی (هفته جاری) - مدت جلسه: {sessionDuration} دقیقه
      </Text>
      <Table size="sm" variant="simple" minW="auto">
        <Thead>
          <Tr>
            <Th borderColor={borderColor} minW="80px">ساعت</Th>
            {workingDays.map((day, idx) => (
              <Th 
                key={idx} 
                borderColor={borderColor} 
                textAlign="center"
                bg={day.isWorking ? 'transparent' : 'gray.50'}
              >
                <Text fontSize="xs" fontWeight={day.isWorking ? 'bold' : 'normal'} color={day.isWorking ? 'inherit' : 'gray.400'}>
                  {PERSIAN_DAYS[day.date.getDay()]}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {day.date.toLocaleDateString('fa-IR', { month: '2-digit', day: '2-digit' })}
                </Text>
                {day.schedule && day.schedule.startTime && day.schedule.endTime && (
                  <Text fontSize="10px" color="gray.400" mt={1}>
                    {day.schedule.startTime} - {day.schedule.endTime}
                  </Text>
                )}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {dynamicHours.map((slot) => (
            <Tr key={slot.timeStr}>
              <Td borderColor={borderColor} fontWeight="medium">
                {slot.timeStr}
              </Td>
              {workingDays.map((day, idx) => {
                const status = getStatusForSlot(day.date, slot);
                return (
                  <Td 
                    key={idx} 
                    borderColor={borderColor} 
                    textAlign="center"
                    bg={!day.isWorking ? 'gray.50' : 'transparent'}
                  >
                    <Box>
                      <Badge 
                        colorScheme={status.color} 
                        fontSize="0.7em"
                        opacity={!day.isWorking ? 0.5 : 1}
                      >
                        {status.label}
                      </Badge>
                      {status.appointment?.clientName && (
                        <Text fontSize="xs" mt={1} color={textColor} noOfLines={2}>
                          {status.appointment.clientName}
                        </Text>
                      )}
                    </Box>
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

export default ConsultantWeeklySchedule;